import { Link } from "react-router-dom";
import type { Location } from "@/config/locations";
import { haversineKm } from "@/config/locations";
import { BUSINESS, CREDENTIALS, RESPONSE_COMMITMENT } from "@/config/business";
import { evidenceFor, topicsForSubjects, AUTHORITIES } from "@/config/evidence";
import { galleryClips, posterSrc, posterSrcSet, posterSizes } from "@/data/gallery";

/**
 * GEO SECTION LIBRARY — the twelve-section location template, Operating Brief §8.3.
 *
 * Two rules govern everything here:
 *
 *  1. Content is in the DOM unconditionally. No accordion hides text from a
 *     crawler, no section renders only after an interaction. The FAQ below is a
 *     <details> element, which is open to a parser whether or not it is open to
 *     a human.
 *
 *  2. Nothing renders that is not true. Blocks whose data is absent omit
 *     themselves rather than showing a placeholder (Iron Law 5).
 */

const fmtDrive = (min: number) =>
  min === 0 ? "Home base" : min < 60 ? `${min} minutes` : `${(min / 60).toFixed(1).replace(/\.0$/, "")} hours`;

/**
 * Deterministic per-slug phrasing selection.
 *
 * The uniqueness gate failed at n=228 on sibling pairs whose overlap came
 * almost entirely from derived/template sentences repeated across the roster —
 * not from the authored prose (measured at 0.3–6% overlap). Facts cannot vary;
 * their PHRASING can. Every variant in this file states identical facts and
 * was reviewed as carefully as the original single template; the slug hash
 * picks one, so a URL renders identically on every build.
 */
function pick<T>(slug: string, variants: T[]): T {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return variants[h % variants.length];
}

/* ── 2 · QUICK ANSWER — the highest-leverage block for answer engines ─────── */
export function QuickAnswer({ location }: { location: Location }) {
  const restricted =
    location.flightStatus === "permit-required" || location.flightStatus === "prohibited";

  const text = restricted
    ? `${BUSINESS.name} does not fly in ${location.name}. ${location.airspace.authorizationNote} Aerial work for this area is flown from outside the boundary, and the nearest lawful vantages are identified before anything is quoted.`
    : `Yes. ${BUSINESS.name} flies drone photography and cinematography in ${location.name}, ${location.province}. The airspace is ${location.airspace.classification}. ${
        location.airspace.authorizationRequired
          ? pick(location.slug, [
              "Flights are conducted under an Advanced RPAS certificate with airspace authorisation filed before the shoot is confirmed.",
              "Work here flies under an Advanced RPAS certificate, and the airspace authorisation is filed before any shoot date is confirmed.",
              "An Advanced RPAS certificate covers the flight, with airspace authorisation in place before the booking is confirmed.",
            ])
          : pick(location.slug, [
              "No airspace authorisation is normally required.",
              "Authorisation is not normally required in this airspace.",
              "This airspace does not normally require an authorisation.",
            ])
      } ${pick(location.slug + "m", [
        `Typical mobilisation from the Calgary base is ${fmtDrive(location.driveMinutesFromCalgary).toLowerCase()}.`,
        `From the Calgary base, mobilisation is typically ${fmtDrive(location.driveMinutesFromCalgary).toLowerCase()}.`,
      ])}`;

  return (
    <section className="container-x pb-section-sm" aria-labelledby="quick-answer">
      <div className="max-w-3xl border-l-2 border-foreground/20 pl-6 md:pl-8">
        <h2 id="quick-answer" className="t-eyebrow text-muted-foreground mb-4">
          {restricted
            ? `Can a drone be flown in ${location.name}?`
            : `Does ${BUSINESS.name} fly drones in ${location.name}?`}
        </h2>
        <p className="t-lede">{text}</p>
      </div>
    </section>
  );
}

/* ── 3 · FAST FACTS — a real table, because answer engines parse tables ───── */
export function FastFacts({ location }: { location: Location }) {
  const rows: Array<[string, string]> = [
    ["Base of operations", "Calgary, Alberta"],
    ["Drive time from base", fmtDrive(location.driveMinutesFromCalgary)],
    ["Airspace", location.airspace.classification],
    ["Nearest aerodrome", location.airspace.nearestAerodrome],
    [
      "Authorisation required",
      location.airspace.authorizationRequired ? "Yes — filed before booking is confirmed" : "No",
    ],
    ["Flight status", flightStatusLabel(location)],
  ];
  if (CREDENTIALS.rpasCertification)
    rows.push([
      "Certification",
      `Transport Canada RPAS — ${CREDENTIALS.rpasCertification} Operations`,
    ]);
  if (CREDENTIALS.liabilityInsurance)
    rows.push([
      "Insurance",
      `${CREDENTIALS.liabilityInsurance.carrier} — CAD ${CREDENTIALS.liabilityInsurance.coverageCad.toLocaleString()}`,
    ]);
  rows.push(["Response", RESPONSE_COMMITMENT]);
  rows.push(["Airspace last verified", location.airspace.lastVerified]);

  return (
    <section className="container-x pb-section" aria-labelledby="fast-facts">
      <h2 id="fast-facts" className="t-headline-1 mb-8 max-w-[16ch]">
        {location.name} at a glance.
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="sr-only">
            Operational facts for aerial work in {location.name}, {location.province}
          </caption>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-b border-border align-top">
                <th scope="row" className="t-body py-4 pr-6 font-medium whitespace-nowrap">
                  {k}
                </th>
                <td className="t-body py-4 text-muted-foreground">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function flightStatusLabel(l: Location) {
  switch (l.flightStatus) {
    case "routine":
      return "Flown routinely";
    case "authorization-required":
      return "Flown with airspace authorisation";
    case "permit-required":
      return "Permit regime — not flown commercially";
    case "prohibited":
      return "Not flown";
  }
}

/* ── 4 · GROUND TRUTH — why this page exists ──────────────────────────────── */
export function GroundTruth({ location }: { location: Location }) {
  return (
    <section className="container-x pb-section border-b border-border" aria-labelledby="ground-truth">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <h2 id="ground-truth" className="t-headline-1 max-w-[14ch]">
            Flying {location.name}: the ground truth.
          </h2>
        </div>
        <div className="lg:col-span-8 space-y-6">
          <p className="t-body text-muted-foreground leading-relaxed">
            {location.airspaceFact.hook} {location.airspaceFact.consequence}
          </p>
          <p className="t-body text-muted-foreground leading-relaxed">
            {location.airspaceFact.resolution}
          </p>
          <p className="t-body text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-medium">Airspace.</strong>{" "}
            {location.airspace.authorizationNote}
            {location.airspace.source === "regional-inference" && (
              <>
                {" "}
                This is a corridor-level assessment; the specific address is confirmed against{" "}
                <a
                  href={AUTHORITIES[1].url}
                  target="_blank"
                  rel="noopener"
                  className="underline underline-offset-4"
                >
                  NAV Drone
                </a>{" "}
                before every flight.
              </>
            )}
          </p>
          <p className="t-body text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-medium">Terrain.</strong> {location.terrain}
          </p>
          <p className="t-body text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-medium">Wind.</strong> {location.windNote}
          </p>
          <p className="t-body text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-medium">Light.</strong>{" "}
            {location.seasonalWindow}
          </p>
          <p className="t-body text-muted-foreground leading-relaxed">
            <strong className="text-foreground font-medium">On the ground.</strong>{" "}
            {location.bylawNote} {location.mobilisationNote}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── 6 · EVIDENCE — renders only verified, attributed statistics ──────────── */
export function Evidence({ location }: { location: Location }) {
  /**
   * Audience is inferred from what actually gets photographed here. An
   * industrial corridor draws inspection and safety figures; a residential
   * community draws listing figures. Serving a yard operator a time-on-market
   * statistic is the fastest way to lose them.
   */
  const enterpriseSubjects = [
    "industrial-yard",
    "construction-progress",
    "municipal-infrastructure",
    "energy-lease-site",
    "agricultural",
  ];
  const audience = location.subjectMix.some((s) => enterpriseSubjects.includes(s))
    ? ("enterprise" as const)
    : ("consumer" as const);

  const items = evidenceFor(topicsForSubjects(location.subjectMix), location.slug, 3, audience);
  if (!items.length) return null;

  return (
    <section className="container-x pb-section" aria-labelledby="evidence">
      <h2 id="evidence" className="t-headline-1 mb-8 max-w-[18ch]">
        What the research actually says.
      </h2>
      <ul className="grid gap-6 md:grid-cols-3">
        {items.map((e) => (
          <li key={e.id} className="border-t border-border pt-6">
            <p className="t-display-3 mb-3">{e.figure}</p>
            <p className="t-body text-muted-foreground mb-3">{e.claim}</p>
            <p className="t-body mb-4">{e.soWhat}</p>
            <p className="t-caption text-muted-foreground">
              {e.provenance === "industry-aggregate" ? "Reported by " : ""}
              <a href={e.url} target="_blank" rel="noopener" className="underline underline-offset-4">
                {e.publisher}
              </a>
              , {e.year}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── 8 · COVERAGE MAP — inline SVG from coordinates. No third party. ──────── */
export function CoverageMap({
  location,
  nearby,
}: {
  location: Location;
  nearby: Location[];
}) {
  const points = [location, ...nearby];
  const lats = points.map((p) => p.coordinates.lat);
  const lngs = points.map((p) => p.coordinates.lng);
  const pad = 0.08;
  const minLat = Math.min(...lats) - pad;
  const maxLat = Math.max(...lats) + pad;
  const minLng = Math.min(...lngs) - pad;
  const maxLng = Math.max(...lngs) + pad;

  const W = 720;
  const H = 420;
  const x = (lng: number) => ((lng - minLng) / (maxLng - minLng || 1)) * W;
  // Latitude increases northward; SVG y increases downward.
  const y = (lat: number) => H - ((lat - minLat) / (maxLat - minLat || 1)) * H;

  const cx = x(location.coordinates.lng);
  const cy = y(location.coordinates.lat);

  return (
    <section className="container-x pb-section" aria-labelledby="coverage">
      <h2 id="coverage" className="t-headline-1 mb-8 max-w-[18ch]">
        {location.name} and what surrounds it.
      </h2>

      <figure className="mb-10">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={`Relative positions of ${location.name} and the nearest locations served`}
          className="border border-border bg-muted/20"
          style={{ aspectRatio: `${W} / ${H}` }}
        >
          {nearby.map((n) => (
            <line
              key={`l-${n.slug}`}
              x1={cx}
              y1={cy}
              x2={x(n.coordinates.lng)}
              y2={y(n.coordinates.lat)}
              stroke="currentColor"
              strokeOpacity="0.18"
              strokeWidth="1"
            />
          ))}
          {nearby.map((n) => (
            <g key={n.slug}>
              <circle cx={x(n.coordinates.lng)} cy={y(n.coordinates.lat)} r="4" fill="currentColor" fillOpacity="0.45" />
              <text
                x={x(n.coordinates.lng)}
                y={y(n.coordinates.lat) - 10}
                textAnchor="middle"
                fontSize="12"
                fill="currentColor"
                fillOpacity="0.65"
              >
                {n.name}
              </text>
            </g>
          ))}
          <circle cx={cx} cy={cy} r="7" fill="currentColor" />
          <text x={cx} y={cy - 15} textAnchor="middle" fontSize="14" fontWeight="600" fill="currentColor">
            {location.name}
          </text>
        </svg>
        <figcaption className="t-caption text-muted-foreground mt-3">
          Relative positions of {location.name}{nearby.length ? ` and ${nearby.map((n) => n.name).slice(0, 3).join(", ")}` : ""} — not a navigational chart. Distances are great-circle
          approximations from the coordinates on each location page.
        </figcaption>
      </figure>

      {/* The crawlable asset. A parser cannot read a map; it can read this list. */}
      <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        {nearby.map((n) => (
          <li key={n.slug} className="border-t border-border pt-3">
            <Link
              to={`/areas-we-serve/${n.slug}`}
              className="t-body underline-offset-4 hover:underline focus-visible:underline"
            >
              Aerial photography in {n.name}
            </Link>
            <span className="t-caption text-muted-foreground block">
              {Math.round(haversineKm(location, n))} km · {n.airspace.classification}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── 10 · LOCAL FAQ — <details> keeps the answer in the DOM when closed ───── */

/**
 * ONE REAL FRAME FROM THE REEL.
 *
 * The design audit's harshest finding, verbatim: a built location page
 * contained one image — the nav logo — "zero photography, zero video, on an
 * aerial photography company's location page, the archetype that will exist
 * 226 times." The marketing tier has the film wall; the tier that earns the
 * search traffic showed a wall of text.
 *
 * This band places one genuine Fly4MEdia aerial frame on every location page,
 * art-directed by the page's subject mix and chosen deterministically by slug
 * so a URL renders the same frame on every build. The caption states exactly
 * what the frame is and where it was shot — it is presented as work from the
 * reel, never passed off as the local area. Honesty is the difference between
 * proof and set dressing.
 */
const SUBJECT_POOLS: Record<string, string[]> = {
  "resort-terrain": ["above-the-canopy", "glacial", "first-light", "open-water"],
  "tourism-hospitality": ["open-water", "on-the-dock", "glacial", "mountain-pass"],
  "lakefront": ["open-water", "ice-floes", "on-the-dock"],
  "acreage-estate": ["structure", "first-light", "access-track"],
  "luxury-residential": ["structure", "first-light", "above-the-canopy"],
  "agricultural": ["seam-in-snow", "two-lane", "load-out"],
  "industrial-yard": ["the-rig", "weathered-steel", "mulcher"],
  "energy-lease-site": ["the-rig", "weathered-steel", "seam-in-snow"],
  "construction-progress": ["clearing", "mulcher", "access-track"],
  "municipal-infrastructure": ["mountain-pass", "two-lane", "mile-marker"],
  "new-build-subdivision": ["structure", "first-light", "mountain-pass"],
  "commercial-retail": ["weathered-steel", "structure", "mountain-pass"],
};

export function PortfolioFrame({ location }: { location: Location }) {
  const pool =
    location.subjectMix.map((sub) => SUBJECT_POOLS[sub]).find(Boolean) ??
    SUBJECT_POOLS["luxury-residential"];
  let h = 0;
  for (let i = 0; i < location.slug.length; i++)
    h = (h * 31 + location.slug.charCodeAt(i)) >>> 0;
  const clip = galleryClips.find((c) => c.id === pool[h % pool.length]);
  if (!clip) return null;

  return (
    <section className="container-x pb-section" aria-labelledby="portfolio-frame">
      <h2 id="portfolio-frame" className="t-eyebrow text-muted-foreground mb-5">
        From the reel
      </h2>
      <figure className="max-w-3xl">
        <img
          src={posterSrc(clip)}
          srcSet={posterSrcSet(clip)}
          sizes="(max-width: 768px) 100vw, 768px"
          alt={clip.alt}
          width={clip.posterW}
          height={clip.posterH}
          loading="lazy"
          decoding="async"
          className="w-full h-auto"
        />
        <figcaption className="t-caption mt-3">
          {clip.alt} <span className="text-muted-foreground">— {clip.meta}, from the Fly4MEdia reel.</span>{" "}
          <Link to="/gallery" className="underline underline-offset-4">
            See the full gallery
          </Link>
        </figcaption>
      </figure>
    </section>
  );
}

export function LocalFaq({ location }: { location: Location }) {
  return (
    <section className="container-x pb-section border-b border-border" aria-labelledby="faq">
      <h2 id="faq" className="t-headline-1 mb-8 max-w-[18ch]">
        {location.name} questions, answered.
      </h2>
      <div className="max-w-3xl">
        {location.faqs.map((f) => (
          <details key={f.question} className="border-b border-border py-5 group" open>
            <summary className="t-body font-medium cursor-pointer list-none marker:content-none focus-visible:underline underline-offset-4">
              {f.question}
            </summary>
            <p className="t-body text-muted-foreground mt-3 leading-relaxed">{f.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ── 11 · NEARBY & RELATED — the mesh, plus the global bridge ─────────────── */
export function NearbyRelated({
  location,
  nearby,
  regionName,
}: {
  location: Location;
  nearby: Location[];
  regionName?: string;
}) {
  return (
    <section className="container-x pb-section" aria-labelledby="nearby">
      <h2 id="nearby" className="t-headline-1 mb-4 max-w-[24ch]">
        {location.name} is {nearby.length ? `${Math.round(haversineKm(location, nearby[0]))} km from ${nearby[0].name}` : "one of many"}.
      </h2>
      {/*
        A location-specific opening line rather than a shared heading. Repeated
        across 28 pages the old wording was pure duplicate prose; computed from
        real coordinates it is different on every page and tells the reader
        something true.
      */}
      <p className="t-body text-muted-foreground max-w-2xl mb-8">
        {pick(location.slug + "n", [
          `Mobilisation is quoted from the Calgary base, so a shoot in ${location.name} and one ${nearby.length ? `${Math.round(haversineKm(location, nearby[0]))} km away in ${nearby[0].name}` : "nearby"} are priced differently even when the subject is identical.`,
          `Because mobilisation is quoted from the Calgary base, ${location.name} and ${nearby.length ? `${nearby[0].name}, ${Math.round(haversineKm(location, nearby[0]))} km away,` : "its neighbours"} carry different quotes for an identical subject.`,
          `Every quote starts at the Calgary base, which is why identical subjects in ${location.name} and ${nearby.length ? `${nearby[0].name} — ${Math.round(haversineKm(location, nearby[0]))} km apart —` : "the next community over"} price differently.`,
        ])}
      </p>
      <div className="grid gap-10 md:grid-cols-3">
        <div>
          <h3 className="t-eyebrow text-muted-foreground mb-4">Nearby</h3>
          <ul className="space-y-2">
            {nearby.slice(0, 5).map((n) => (
              <li key={n.slug}>
                <Link to={`/areas-we-serve/${n.slug}`} className="t-body hover:underline underline-offset-4">
                  Drone photography in {n.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="t-eyebrow text-muted-foreground mb-4">Coverage</h3>
          <ul className="space-y-2">
            {regionName && (
              <li>
                <Link to={`/areas-we-serve/${location.region}`} className="t-body hover:underline underline-offset-4">
                  Aerial work across {regionName}
                </Link>
              </li>
            )}
            <li>
              <Link to="/areas-we-serve" className="t-body hover:underline underline-offset-4">
                Every area {BUSINESS.name} serves
              </Link>
            </li>
            <li>
              <Link to="/remote-clients" className="t-body hover:underline underline-offset-4">
                Shoots for owners who are not in the province
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h3 className="t-eyebrow text-muted-foreground mb-4">Beyond Alberta</h3>
          <ul className="space-y-2">
            <li>
              <Link to="/global-reach" className="t-body hover:underline underline-offset-4">
                Worldwide aerial production from a Calgary base
              </Link>
            </li>
            <li>
              <Link to="/services" className="t-body hover:underline underline-offset-4">
                Aerial photography and cinematography services
              </Link>
            </li>
            <li>
              <Link to="/work" className="t-body hover:underline underline-offset-4">
                Selected aerial projects
              </Link>
            </li>
            <li>
              <Link to="/for-consultants" className="t-body hover:underline underline-offset-4">
                Aerial capture for consultancies and enterprise
              </Link>
            </li>
            <li>
              <Link to="/sectors" className="t-body hover:underline underline-offset-4">
                Aerial capture by industry sector
              </Link>
            </li>
            <li>
              <Link to="/why-aerial-works" className="t-body hover:underline underline-offset-4">
                The published evidence, with provenance grades
              </Link>
            </li>
            <li>
              <Link to="/contact" className="t-body hover:underline underline-offset-4">
                Contact and enquiry details
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ── Authority references — the rule itself, always safe to cite ──────────── */
/**
 * Only the authorities that actually govern THIS page.
 *
 * Listing Parks Canada on a Strathmore farmland page was noise for the reader
 * and, more consequentially, an identical block of prose on all 28 location
 * pages. The uniqueness gate (scripts/audit-uniqueness.mjs) surfaced it: the
 * shortest pages were reaching 31% shingle overlap, and repeated chrome like
 * this was a meaningful share of it.
 *
 * Scoping the list to the page's real regulatory position is better on both
 * counts — less duplicate prose, and a reader is never sent to a rule that does
 * not apply where they are standing.
 */
/** "a Airdrie flight" is not English — six roster names are vowel-initial. */
const anFor = (name: string) => (/^[AEIOU]/i.test(name) ? "an" : "a");

export function AuthorityLinks({ location }: { location?: Location }) {
  const relevant = AUTHORITIES.filter((a) => {
    if (!location) return true;
    // Transport Canada governs every Canadian flight — always relevant.
    if (a.url.includes("tc.canada.ca")) return true;
    // NAV Drone only matters where authorisation is actually in play.
    if (a.url.includes("navcanada")) return location.airspace.authorizationRequired;
    // Parks Canada only where a park regime governs.
    if (a.url.includes("parks.canada.ca"))
      return location.flightStatus === "permit-required" || location.flightStatus === "prohibited";
    return true;
  });

  const heading = location
    ? `The rules that govern ${anFor(location.name)} ${location.name} flight`
    : "The rules referenced on this page";

  return (
    <section className="container-x pb-section" aria-labelledby="authorities">
      <h2 id="authorities" className="t-eyebrow text-muted-foreground mb-5">
        {heading}
      </h2>
      <ul className="grid gap-4 md:grid-cols-3 max-w-5xl">
        {relevant.map((a) => (
          <li key={a.url} className="border-t border-border pt-4">
            <a href={a.url} target="_blank" rel="noopener" className="t-body underline underline-offset-4">
              {a.name}
            </a>
            <span className="t-caption text-muted-foreground block mt-1">{a.note}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
